/**
 * Screen-space ↔ world-space brush radius conversion.
 *
 * All brush radii (sculpt, sketch) and the proportional editing radius
 * are stored in screen-space CSS pixels.  At the point of use — handlers,
 * cursors, influence circle — they are converted to world-space (or
 * object-local) units using the active camera projection.
 *
 * The module caches the latest camera state so non-R3F code (store ops,
 * keyboard shortcuts) can perform approximate conversions without direct
 * camera access.
 */

import * as THREE from "three/webgpu";

// ---------------------------------------------------------------------------
// Module-level camera cache — updated per-frame by R3F components
// ---------------------------------------------------------------------------

let _camera: THREE.Camera | null = null;
let _viewportHeight = 0;

/**
 * Update the cached camera state.  Call from any R3F `useFrame` callback
 * to keep the conversion available for non-R3F code paths.
 */
export function setCameraState(
  camera: THREE.Camera,
  viewportHeight: number,
): void {
  _camera = camera;
  _viewportHeight = viewportHeight;
}

// ---------------------------------------------------------------------------
// Conversion functions
// ---------------------------------------------------------------------------

/**
 * Convert a screen-space radius in CSS pixels to a world-space radius
 * at the depth of `worldPoint`.
 *
 * For PerspectiveCamera the result scales linearly with depth (distance
 * from camera to the world point).
 * For OrthographicCamera the result is constant regardless of depth.
 *
 * When `camera` / `viewportHeight` are omitted, the cached values from
 * the most recent `setCameraState` call are used — suitable for store-level
 * code that lacks direct R3F access.
 */
export function screenToWorldRadius(
  screenPx: number,
  worldPoint: THREE.Vector3,
  camera?: THREE.Camera,
  viewportHeight?: number,
): number {
  const cam = camera ?? _camera;
  const vh = viewportHeight ?? _viewportHeight;
  if (!cam || vh <= 0) return screenPx * 0.01;

  if ((cam as THREE.PerspectiveCamera).isPerspectiveCamera) {
    const persp = cam as THREE.PerspectiveCamera;
    const distance = persp.position.distanceTo(worldPoint);
    const vFov = (persp.fov * Math.PI) / 180;
    return (screenPx * (2 * distance * Math.tan(vFov / 2))) / vh;
  }

  if ((cam as THREE.OrthographicCamera).isOrthographicCamera) {
    const ortho = cam as THREE.OrthographicCamera;
    return (screenPx * (ortho.top - ortho.bottom)) / vh;
  }

  return screenPx * 0.01;
}

// Reusable temporaries for matrix decomposition (avoids per-call allocation)
const _decomposePos = new THREE.Vector3();
const _decomposeQuat = new THREE.Quaternion();
const _decomposeScale = new THREE.Vector3();

/**
 * Convert a screen-space radius to object-local units at a world-space
 * point, accounting for the mesh's cumulative world transform.
 *
 * Combines `screenToWorldRadius` with a scale correction derived from
 * the mesh's `matrixWorld`.  Non-uniform scale is approximated by
 * averaging the three axis scales.
 */
export function screenToObjectLocalRadius(
  screenPx: number,
  worldPoint: THREE.Vector3,
  meshMatrixWorld: THREE.Matrix4,
  camera?: THREE.Camera,
  viewportHeight?: number,
): number {
  const worldRadius = screenToWorldRadius(
    screenPx,
    worldPoint,
    camera,
    viewportHeight,
  );
  meshMatrixWorld.decompose(_decomposePos, _decomposeQuat, _decomposeScale);
  const avgScale =
    (_decomposeScale.x + _decomposeScale.y + _decomposeScale.z) / 3;
  return avgScale > 1e-8 ? worldRadius / avgScale : worldRadius;
}
